import { useCallback, useEffect, useState } from 'react';
import { api, ErroApi } from '../lib/api';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import Layout from '../componentes/Layout';

type Visitante = {
  id: number;
  nome: string;
  whatsapp: string;
  instagram: string | null;
  sexo: string | null;
  equipe: string | null;
  interesse: string | null;
  observacao: string | null;
  situacao: string;
  anotacoes: string | null;
  atendidoPor: string | null;
  criadoEm: string | null;
};

const SITUACOES = [
  { valor: '', rotulo: 'Todos' },
  { valor: 'novo', rotulo: 'Novos' },
  { valor: 'contatado', rotulo: 'Contatados' },
  { valor: 'integrado', rotulo: 'Integrados' },
  { valor: 'arquivado', rotulo: 'Arquivados' },
];

const CORES: Record<string, string> = {
  novo: 'bg-amber-100 text-amber-800',
  contatado: 'bg-sky-100 text-sky-800',
  integrado: 'bg-tinta-900 text-white',
  arquivado: 'bg-tinta-100 text-tinta-600',
};

const quando = (iso: string | null) =>
  iso
    ? new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(iso))
    : '';

/** So numeros, para montar o link do WhatsApp. */
const soDigitos = (t: string) => t.replace(/\D/g, '');

export default function Visitantes() {
  const [situacao, setSituacao] = useState('');
  const [lista, setLista] = useState<Visitante[]>([]);
  const [contagem, setContagem] = useState<Record<string, number>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<number | null>(null);
  const [anotacao, setAnotacao] = useState('');
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const d = await api<{ visitantes: Visitante[]; contagem: Record<string, number> }>(
        `/visitantes${situacao ? `?situacao=${situacao}` : ''}`,
      );
      setLista(d.visitantes);
      setContagem(d.contagem);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os visitantes.');
    }
  }, [situacao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function mudar(v: Visitante, campos: Record<string, unknown>) {
    try {
      await api(`/visitantes/${v.id}`, { method: 'PATCH', body: JSON.stringify(campos) });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    }
  }

  const endereco = `${window.location.origin}/formulario`;

  return (
    <Layout
      titulo="Visitantes"
      subtitulo="Quem visitou a igreja e deixou contato. Dado sensivel: so a liderança ve."
    >
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <Cartao className="mb-4 max-w-3xl">
        <h2 className="font-semibold text-tinta-900">Link do formulario</h2>
        <p className="mt-1 text-sm text-tinta-500">
          Coloque no QR Code do culto, no story ou na recepcao. Quem preencher cai aqui na hora.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            readOnly
            value={endereco}
            onFocus={(e) => e.currentTarget.select()}
            className="min-w-0 flex-1 rounded-xl border border-tinta-200 px-3 py-2 text-sm text-tinta-600"
          />
          <Botao
            variante="secundario"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(endereco);
              } catch {
                setErro('Não consegui copiar. Selecione o texto e copie na mao.');
              }
            }}
          >
            Copiar
          </Botao>
          <a
            href="/formulario"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl border border-tinta-200 px-4 py-2.5 text-sm font-semibold text-tinta-700 hover:bg-tinta-100"
          >
            Abrir
          </a>
        </div>
      </Cartao>

      <div className="mb-4 flex flex-wrap gap-2">
        {SITUACOES.map((s) => (
          <button
            key={s.valor}
            onClick={() => setSituacao(s.valor)}
            className={`rounded-xl px-3.5 py-2 text-sm font-medium transition ${
              situacao === s.valor
                ? 'bg-tinta-900 text-white'
                : 'bg-white text-tinta-600 shadow-cartao hover:bg-tinta-100'
            }`}
          >
            {s.rotulo}
            {s.valor && contagem[s.valor] ? (
              <span className="ml-1.5 text-xs opacity-70">{contagem[s.valor]}</span>
            ) : null}
          </button>
        ))}
      </div>

      {lista.length === 0 ? (
        <Cartao className="max-w-3xl">
          <p className="text-sm text-tinta-400">Nenhum visitante nesta lista ainda.</p>
        </Cartao>
      ) : (
        <div className="max-w-3xl space-y-3">
          {lista.map((v) => (
            <Cartao key={v.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-tinta-900">{v.nome}</p>
                  <p className="text-sm text-tinta-500">
                    {v.whatsapp}
                    {v.instagram ? ` · @${v.instagram}` : ''}
                    {v.sexo ? ` · ${v.sexo === 'prefiro_nao_dizer' ? 'não informado' : v.sexo}` : ''}
                  </p>
                  <p className="mt-0.5 text-xs text-tinta-400">
                    chegou em {quando(v.criadoEm)}
                    {v.atendidoPor ? ` · atendido por ${v.atendidoPor}` : ''}
                  </p>

                  {(v.equipe || v.interesse) && (
                    <p className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
                      {v.equipe && (
                        <span className="rounded-md bg-tinta-100 px-2 py-0.5 text-tinta-600">
                          equipe: {v.equipe}
                        </span>
                      )}
                      {v.interesse && (
                        <span className="rounded-md bg-tinta-900 px-2 py-0.5 font-medium text-white">
                          quer servir: {v.interesse}
                        </span>
                      )}
                    </p>
                  )}

                  {v.observacao && (
                    <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-100">
                      <b className="block text-xs uppercase tracking-wide text-amber-700">
                        Pedido de oracao
                      </b>
                      {v.observacao}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${CORES[v.situacao] ?? ''}`}
                  >
                    {v.situacao}
                  </span>
                  <a
                    href={`https://wa.me/55${soDigitos(v.whatsapp)}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="rounded-xl bg-tinta-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-tinta-700"
                  >
                    Falar no WhatsApp
                  </a>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-tinta-200 pt-3">
                {(['novo', 'contatado', 'integrado', 'arquivado'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => void mudar(v, { situacao: s })}
                    disabled={v.situacao === s}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                      v.situacao === s
                        ? 'bg-tinta-900 text-white'
                        : 'bg-tinta-100 text-tinta-600 hover:bg-tinta-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}

                <button
                  onClick={() => {
                    setAberto(aberto === v.id ? null : v.id);
                    setAnotacao(v.anotacoes ?? '');
                  }}
                  className="ml-auto text-xs font-medium text-tinta-400 hover:text-tinta-900"
                >
                  {v.anotacoes ? 'ver anotações' : 'anotar'}
                </button>

                <button
                  onClick={async () => {
                    const ok = await confirmar({
                      titulo: `Apagar ${v.nome} da lista?`,
                      texto: 'Os dados dessa pessoa somem do sistema. Não da para desfazer.',
                      confirmar: 'Apagar',
                      perigo: true,
                    });
                    if (!ok) return;
                    try {
                      await api(`/visitantes/${v.id}`, { method: 'DELETE' });
                      await carregar();
                    } catch (e) {
                      setErro(e instanceof ErroApi ? e.message : 'Não consegui apagar.');
                    }
                  }}
                  className="text-xs text-tinta-400 hover:text-red-600"
                >
                  apagar
                </button>
              </div>

              {aberto === v.id && (
                <div className="mt-3 space-y-2">
                  <textarea
                    rows={3}
                    value={anotacao}
                    onChange={(e) => setAnotacao(e.target.value)}
                    placeholder="Como foi o contato, o que combinamos..."
                    className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                  />
                  <Botao
                    onClick={async () => {
                      await mudar(v, { anotacoes: anotacao.trim() || null });
                      setAberto(null);
                    }}
                  >
                    Salvar anotacao
                  </Botao>
                </div>
              )}
            </Cartao>
          ))}
        </div>
      )}
      {dialogo}
    </Layout>
  );
}
